import { useLocation } from 'wouter';
import { api, type Health, type Stats } from '../lib/api';
import { usd } from '../lib/time';
import type { Resource } from '../lib/use-resource';
import type { ConfirmRequest } from './Confirm';
import './header.css';

/**
 * The top bar: who is running, what it has spent today, and the stop switch.
 *
 * THE HALT SWITCH IS THE POINT OF THIS BAR. v1 had no single switch at all:
 * the operator said to send nothing and at least four lifecycle emails went
 * out while policies were edited one at a time. "A hold that takes hours to
 * honour is not a hold." It is on every screen, and it always confirms —
 * turning it OFF lets the whole system send again, which is the direction
 * that can surprise somebody.
 */
export function Header({ stats, health, onConfirm, onChanged }: {
  stats: Resource<Stats>;
  health: Resource<Health>;
  onConfirm: (r: ConfirmRequest) => void;
  onChanged: () => void;
}) {
  const [, setLocation] = useLocation();
  const halted = stats.data?.outbound_halt ?? false;

  const toggleHalt = () => onConfirm({
    title: halted ? 'Let the system send again?' : 'Stop every outbound email?',
    detail: halted
      ? 'Lifecycle emails, drafted replies and rescue outreach all resume immediately. '
        + 'Cards approved while the halt was on were refused, not queued — they stay where they are.'
      : 'Nothing reaches a customer until you turn this back on. Approving a card that sends will '
        + 'be refused with the halt as the reason, and rules keep raising cards as normal.',
    confirmLabel: halted ? 'Outbound on' : 'Halt outbound',
    tone: halted ? 'normal' : 'danger',
    askReason: true,
    reasonLabel: 'Why (recorded as an event)',
    onConfirm: async (reason) => {
      await api.halt(!halted, reason || undefined);
      onChanged();
    },
  });

  return (
    <header className="top">
      <button type="button" className="wordmark" onClick={() => setLocation('/')}>
        <img className="wordmark-logo" src="/brand/click2call-logo.svg" alt="Click2Call" width={103} height={28} />
        <span className="wordmark-kind mono">Desk</span>
      </button>

      <WorkerHealth health={health} />

      <div className="spend mono" title="Model spend since midnight UTC, from model_calls">
        <span className="spend-label">Spend today</span>
        <span className="spend-value">{usd(stats.data?.model_spend_today_usd)}</span>
      </div>

      <button
        type="button"
        className={`halt ${halted ? 'is-halted' : ''}`}
        onClick={toggleHalt}
        disabled={!stats.data}
        aria-label={halted ? 'Outbound is halted. Let the system send again' : 'Halt all outbound email'}
      >
        <span className="halt-track"><span className="halt-knob" /></span>
        <span>{halted ? 'Outbound HALTED' : 'Outbound on'}</span>
      </button>
    </header>
  );
}

/**
 * "ledger 14m · inbox ok · desk ok".
 *
 * Read from the `runs` row each worker writes, not from a ping. desk's cron is
 * load bearing for three things — the veto drain, the one automatic fraud
 * action, and Slack posting — so a desk cron that has not run is not a
 * curiosity, it is cards silently not happening.
 */
function WorkerHealth({ health }: { health: Resource<Health> }) {
  const workers = health.data?.workers ?? [];
  const bad = workers.some((w) => w.state === 'failed' || w.state === 'stalled');
  // `late` is the desk's own answer now — a run that succeeded and is older
  // than that worker's tolerance, which the desk reads from the same settings
  // row the health run uses. The hard-coded 90 minutes that used to stand in
  // for it is gone: two copies of "how old is too old" would drift, and this
  // one could not know that desk runs every ten minutes and ledger hourly.
  const warn = workers.some((w) => w.state === 'never' || w.state === 'late');

  return (
    <div className="health mono" title="The last run of each worker, from the runs table">
      <span className={`dot ${bad ? 'dot-bad' : warn ? 'dot-warn' : 'dot-ok'}`} />
      <span>
        {workers.length === 0 ? 'health unknown' : workers.map(describe).join(' · ')}
      </span>
    </div>
  );
}

/**
 * A RUNNING WORKER IS NOT A FAILED ONE.
 *
 * An in-flight run has no `ok` yet, and reading that absence as failure put
 * "ledger FAILED" on this header for the five minutes ledger's hourly poll
 * takes — which is what the operator saw the first time he opened the console.
 * The desk now returns a state and this just prints it.
 */
function describe(w: Health['workers'][number]): string {
  switch (w.state) {
    case 'never': return `${w.worker} never run`;
    case 'failed': return `${w.worker} FAILED`;
    case 'stalled': return `${w.worker} STALLED ${w.minutes_ago}m`;
    case 'running': return `${w.worker} running ${w.minutes_ago}m`;
    // Named, not just coloured. "desk 44m" beside "ledger 12m" says nothing
    // about which of the two is wrong, because their cadences differ by six
    // times over.
    case 'late': return `${w.worker} LATE ${w.minutes_ago}m`;
    default: return `${w.worker} ${w.minutes_ago}m`;
  }
}
